import {
    FIELD_FILL_LAYER,
    FIELD_LINE_LAYER,
    IRRIGATION_FILL_LAYER,
    IRRIGATION_LINE_LAYER,
    SECTOR_FILL_LAYER,
    SECTOR_LINE_LAYER,
    SECTOR_TEXT_LAYER,
} from "@/lib/map/layers";
import useIsMobile from "@/lib/utils/useIsMobile";
import { faClose, faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Accordion,
    AccordionItem,
    Button,
    Divider,
    useDisclosure,
} from "@heroui/react";
import SimpleLayer from "./SimpleLayer";
import ImageryLayer from "./ImageryLayer";
import classNames from "classnames";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import { useCallback } from "react";
import RxLayers from "./RxLayers";
import OperationsLayers from "./OperationsLayers";
import ManagementZoneLayers from "./ManagmenetZoneLayers";
import DataLayersMenu from "./DataLayersMenu";

function LayersMenu() {
    const { isOpen, onOpen, onClose } = useDisclosure({});
    const { fieldId, fieldQuery, geometryQuery } = useFieldDashboard() ?? {};

    const { isMobile } = useIsMobile();

    const { downloadFile } = useRequest();

    const downloadIrrigationSystemBoundary = useCallback(async () => {
        if (!fieldQuery?.data?.irrigation_systems?.length) return;
        await downloadFile(
            `irrigation_systems/${fieldQuery?.data?.irrigation_systems[0]?.id}/download_boundary/`,
            `${fieldQuery?.data?.name} Irrigated Area Boundary.zip`,
        );
    }, [
        downloadFile,
        fieldQuery?.data?.irrigation_systems,
        fieldQuery?.data?.name,
    ]);

    const downloadFieldBoundary = useCallback(async () => {
        await downloadFile(
            `fields/${fieldId}/download_boundary/`,
            `${fieldQuery?.data?.name} Field Boundary.zip`,
        );
    }, [downloadFile, fieldId, fieldQuery?.data?.name]);

    const downloadSectors = useCallback(async () => {
        if (!geometryQuery?.data?.irrigation_systems?.features?.length) return;

        await downloadFile(
            `irrigation_systems/${geometryQuery?.data?.irrigation_systems?.features[0]?.id}/download_sectors/`,
            `${fieldQuery?.data?.name} Sectors`,
        );
    }, [
        downloadFile,
        fieldQuery?.data?.name,
        geometryQuery?.data?.irrigation_systems?.features,
    ]);

    const content = (
        <>
            <div className="flex shrink-0 flex-row items-center justify-between p-2">
                <div className="text-xl font-semibold text-ellipsis whitespace-nowrap">
                    <FontAwesomeIcon
                        icon={faLayerGroup}
                        className="pr-2 text-brandNavy-500"
                    />
                    Map Layers
                </div>
                <Button onPress={onClose} isIconOnly variant="solid" size="sm">
                    <FontAwesomeIcon icon={faClose} />
                </Button>
            </div>
            <Divider className="shrink-0" />
            <div className="min-h-0 flex-1 overflow-y-auto">
                <Accordion
                    selectionMode="multiple"
                    isCompact
                    defaultExpandedKeys={[
                        "field",
                        "imagery",
                        "rx",
                        "operations",
                        "management-zones",
                        "data-layers",
                    ]}
                >
                    <AccordionItem
                        key="field"
                        title="Field"
                        classNames={{
                            trigger: "p-1",
                        }}
                    >
                        <SimpleLayer
                            name="Field"
                            layerNames={[
                                FIELD_FILL_LAYER.id,
                                FIELD_LINE_LAYER.id,
                            ]}
                            defaultVisible
                            onDownload={downloadFieldBoundary}
                        />
                        <SimpleLayer
                            name="Pivot Boundary"
                            layerNames={[
                                IRRIGATION_FILL_LAYER.id,
                                IRRIGATION_LINE_LAYER.id,
                            ]}
                            disabled={
                                !geometryQuery?.data?.irrigation_systems
                                    ?.features?.length
                            }
                            onDownload={downloadIrrigationSystemBoundary}
                        />
                        <SimpleLayer
                            name="Sectors"
                            layerNames={[
                                SECTOR_FILL_LAYER.id,
                                SECTOR_LINE_LAYER.id,
                                SECTOR_TEXT_LAYER.id,
                            ]}
                            disabled={
                                !geometryQuery?.data?.treatment_sections
                                    ?.features?.length
                            }
                            onDownload={downloadSectors}
                        />
                    </AccordionItem>
                    <AccordionItem
                        key="imagery"
                        title="Imagery"
                        classNames={{
                            trigger: "p-1",
                        }}
                    >
                        <ImageryLayer />
                    </AccordionItem>
                    <AccordionItem
                        key="rx"
                        title="Rx & Plots"
                        classNames={{
                            trigger: "p-1",
                        }}
                    >
                        <RxLayers />
                    </AccordionItem>
                    <AccordionItem
                        key="operations"
                        title="Operations"
                        classNames={{
                            trigger: "p-1",
                        }}
                    >
                        <OperationsLayers />
                    </AccordionItem>
                    <AccordionItem
                        key="management-zones"
                        title="Management Zones"
                        classNames={{
                            trigger: "p-1",
                        }}
                    >
                        <ManagementZoneLayers />
                    </AccordionItem>
                    <AccordionItem
                        key="data-layers"
                        title="Data Layers"
                        classNames={{
                            trigger: "p-1",
                        }}
                    >
                        <DataLayersMenu />
                    </AccordionItem>
                </Accordion>
            </div>
        </>
    );

    return (
        <>
            <div className="absolute top-[49px] right-[10px] bg-white rounded-[4px] z-[5]">
                <Button
                    variant="solid"
                    color="default"
                    className="shadow-md bg-white w-[29px] h-[29px] p-0 m-0 min-w-0 rounded-[4px]"
                    onPress={onOpen}
                >
                    <FontAwesomeIcon
                        icon={faLayerGroup}
                        className="text-[#11181c]"
                    />
                </Button>
            </div>
            {isMobile ? (
                <div
                    className={classNames({
                        "duration-0 z-50 ": true,
                        "h-0 w-0 hidden": !isOpen,
                        "fixed inset-0 w-screen h-screen bg-black bg-opacity-75":
                            isOpen,
                    })}
                    onClick={onClose}
                >
                    <div
                        className="flex h-[90dvh] w-[90%] flex-col overflow-hidden mt-[5dvh] mx-auto bg-white rounded-md"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {content}
                    </div>
                </div>
            ) : (
                <div
                    style={{
                        height: !isOpen
                            ? 0
                            : "min(40rem, calc(100% - 3.5rem))",
                        width: !isOpen ? 0 : "20rem",
                    }}
                    className="absolute z-50 drop-shadow-2xl overflow-hidden shadow-black bg-white md:right-[10px] md:top-[49px] max-h-[calc(100%-3.5rem)] transition-size duration-200 rounded-md"
                >
                    <div className="flex h-full min-h-0 flex-col">{content}</div>
                </div>
            )}
        </>
    );
}

export default LayersMenu;
